import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import "react-widgets/styles.css";
import {
    Col,
    Row,
    Table
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Affiliation, Entity, EntityStateChangeEvent, getEntityHistory, getSelectedEntity } from '../../redux/EntitySlice';
import dateHelper from '../../utils/dateHelper';
import utilityHelper from "../../utils/utilityHelper";
import EventList from './EventList';
import { Reference } from '../../redux/TaxonomyReferenceSlice';
import { EntityReference, getEntityNameForCode, selectEntities } from '../../redux/EntitiesSlice';

const EntityHistoryViewer = (props: any) => {

    const dispatch = useAppDispatch();
    const entitySelector: Entity | null = useAppSelector(getSelectedEntity);
    var entityList: EntityReference[] = useAppSelector(selectEntities);

    const [entityEventList, setEntityEventList] = React.useState<Array<EntityStateChangeEvent>>([]);

    const createEvent = function(s : string, v : string, ts : string, fname : string, lname : string) : EntityStateChangeEvent {
        var result : EntityStateChangeEvent = {
            state: s,
            value: v,
            timestamp: ts,
            creator: {
                activateDate: "",
                createDate: "",
                deprecateDate: "",
                firstName: fname,
                lastName: lname,
                id: 0,
                lastLoginDate: "",
                login: "",
                role: "",
                status: "",
                entityAuthorizations: []
            },
            reason: null
        };
        return result;
    }
    const createNameChangeEvent = function (r: Reference): EntityStateChangeEvent {
        var d: string = "";
        var n: string = "";
        r.parts.map((x: any) => {
            switch (x.definition.name) {
                case "PreviousEntityName":
                    n = x.value;
                    break;

                case "NameChangeDate":
                    d = x.value;
                    break;
            }
        });
        var result: EntityStateChangeEvent = {
            state: "NAME_CHANGE",
            value: "Was previously_" + n.split(' ').join('_'),
            timestamp: d,
            creator: {
                activateDate: "",
                createDate: "",
                deprecateDate: "",
                firstName: "",
                lastName: "",
                id: 0,
                lastLoginDate: "",
                login: "",
                role: "",
                status: "",
                entityAuthorizations: []
            },
            reason: null
        };
        return result;
    }
    const createDBAEvent = function (entity: Entity, doingBusinessAs: any): EntityStateChangeEvent {
        var d: string = entity.creationDate;
        var v: string = "";
        if (doingBusinessAs.previousEntity === entity.code) {
            v = "Is currently_" + getEntityNameForCode(entityList, doingBusinessAs.currentEntity).split(' ').join('_');
        } else {
            v = "Was previously_" + getEntityNameForCode(entityList, doingBusinessAs.previousEntity).split(' ').join('_');
        }

        var result: EntityStateChangeEvent = {
            state: "DOING_BUSINESS_AS",
            value: v,
            timestamp: d,
            creator: {
                activateDate: "",
                createDate: "",
                deprecateDate: "",
                firstName: "",
                lastName: "",
                id: 0,
                lastLoginDate: "",
                login: "",
                role: "",
                status: "",
                entityAuthorizations: []
            },
            reason: null
        };
        return result;
    }
    const createPrimaryChangeEvent = function (entity: Entity, affiliation: any): EntityStateChangeEvent {
        var d: string = affiliation.effectiveDate;
        var v: string = "";
        if (affiliation.prior && affiliation.prior.affiliate) {
            v = "Was previously_" + affiliation.prior.affiliate.name.split(' ').join('_');
        }

        var result: EntityStateChangeEvent = {
            state: "PRIMARY_GOVERNMENT_CHANGE",
            value: v,
            timestamp: d,
            creator: {
                activateDate: "",
                createDate: "",
                deprecateDate: "",
                firstName: "",
                lastName: "",
                id: 0,
                lastLoginDate: "",
                login: "",
                role: "",
                status: "",
                entityAuthorizations: []
            },
            reason: null
        };
        return result;
    }
    const createCurrentPrimaryChangeEvent = function (entity: Entity, affiliation: any): EntityStateChangeEvent {
        var d: string = affiliation.endDate;
        var v: string = "";
        if (affiliation.endDate && !dateHelper.IsDateInFuture(affiliation.endDate)) {
            v = "Was previously_" + affiliation.affiliate.name.split(' ').join('_');
        }

        var result: EntityStateChangeEvent = {
            state: "PRIMARY_GOVERNMENT_CHANGE",
            value: v,
            timestamp: d,
            creator: {
                activateDate: "",
                createDate: "",
                deprecateDate: "",
                firstName: "",
                lastName: "",
                id: 0,
                lastLoginDate: "",
                login: "",
                role: "",
                status: "",
                entityAuthorizations: []
            },
            reason: null
        };
        return result;
    }
    async function getHistory() {

        setEntityEventList([]);
        if (entitySelector) {
            const response = await dispatch(getEntityHistory(entitySelector.code)).then((result : any) => {
                const eventListing: Array<EntityStateChangeEvent> = unwrapResult(result);

                if (eventListing) {
                    if (entitySelector) {
                        if (entitySelector.affiliation) {
                            var affEntry: Affiliation | null = entitySelector.affiliation;
                            if (affEntry.endDate && !dateHelper.IsDateInFuture(affEntry.endDate)) {
                                eventListing.push(createCurrentPrimaryChangeEvent(entitySelector, affEntry));
                            }
                            while (affEntry) {
                                if (affEntry.prior) {
                                    eventListing.push(createPrimaryChangeEvent(entitySelector, affEntry));
                                }
                                affEntry = affEntry.prior;
                            }
                        }

                        if (entitySelector.references) {
                            entitySelector.references.map(x => (x.type.indexOf("nameChange") > 0) ? eventListing.push(createNameChangeEvent(x)) : "");
                        }
                        if (entitySelector.doingBusinessAs) {
                            eventListing.push(createDBAEvent(entitySelector, entitySelector.doingBusinessAs));
                        }
                    }
                    setEntityEventList(eventListing);
                } 
            }
            );
        }
    }

    React.useEffect(() => {

         getHistory();

    }, [entitySelector])

    function getEventValueAndReason(entityEvent: EntityStateChangeEvent) : string {
        var result: string = utilityHelper.SnakeCaseToTitleCase(entityEvent.value);
        if (entityEvent.reason && entityEvent.reason.length > 0) {
            result += " ( " + entityEvent.reason + " )";
        }
        return result;
    }
    function backgroundClassGivenEventValue(entityEvent: EntityStateChangeEvent): string | undefined {
        let result: string = "";

        switch (entityEvent.value.split(" ")[0]) {
            case "ACTIVE":
            case "CREATED":
            case "TRUE":
                result = "table-success";
                break;
            case "FALSE":
            case "DISSOLVED":
                result = "table-danger";
                break;

            case "IN_PROGRESS":
            case "CERTIFIED_BY_ENTITY":
            case "RE_CERTIFIED_BY_ENTITY":
            case "VERIFIED":
            case "INACTIVE":
            case "INDEPENDENT":
            case "DEPENDENT":
                result = "table-info";
                break;
            default:
                switch (entityEvent.state) {
                    case "NAME_CHANGE":
                        result = "table-info";
                        break;

                    case "PRIMARY_GOVERNMENT_CHANGE":
                        result = "table-info";
                        break;

                    case "DOING_BUSINESS_AS":
                        result = "table-info";
                        break;

                    default:
                        result = "table-light";
                        break;
                }
                break;
        }

        return result;
    }


    return (
        <React.Fragment>
            <Row style={{ "marginTop": "1em" }}>
                <Col sm="12">
                    {

                        entitySelector ?
                            (<>
                                <Table responsive striped hover bordered>
                                    <thead>
                                        <tr>
                                            <th>
                                                State
                                            </th>
                                            <th>
                                                Value
                                            </th>
                                            <th>
                                                Create Time
                                            </th>
                                            <th>
                                                Creator
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entityEventList.map((entityEvent: EntityStateChangeEvent, idx: number) =>
                                            <tr key={idx} className={backgroundClassGivenEventValue(entityEvent)}>
                                                <td>
                                                    {utilityHelper.SnakeCaseToTitleCase(entityEvent.state)}
                                                </td>
                                                <td>
                                                    {getEventValueAndReason(entityEvent)}
                                                </td>
                                                <td>
                                                    {dateHelper.stringToDateString(entityEvent.timestamp)}
                                                </td>
                                                <td>
                                                    {entityEvent.creator.firstName + " " + entityEvent.creator.lastName}
                                                </td>
                                            </tr>

                                        )}
                                    </tbody>
                                </Table>
                            </>) : (<></>)

                    }

                </Col>
            </Row>
        </React.Fragment>
    );
}

export default EntityHistoryViewer;